import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListTrainers,
  useListLocations,
  useGetTrainerAvailability,
  useCreateAvailability,
  useDeleteAvailability,
  getGetTrainerAvailabilityQueryKey,
  type TrainerProfile,
  type AvailabilityBlock,
  type Location,
} from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Plus, Trash2, Users, UserCheck } from "lucide-react";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const totalMinutes = i * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const label = `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
  const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  return { label, value };
});

function formatTimeDisplay(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Failed to add availability block";
}

function TrainerDayRow({
  day,
  dayIndex,
  blocks,
  trainerId,
  gymId,
  locationId,
  onChanged,
}: {
  day: string;
  dayIndex: number;
  blocks: AvailabilityBlock[];
  trainerId: string;
  gymId: string;
  locationId?: string;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [adding, setAdding] = useState(false);

  const dayBlocks = blocks.filter(b => b.dayOfWeek === dayIndex && b.isActive);

  const createMutation = useCreateAvailability();
  const deleteMutation = useDeleteAvailability();

  const handleAdd = async () => {
    if (startTime >= endTime) {
      toast({ title: "Invalid range", description: "Start time must be before end time", variant: "destructive" });
      return;
    }
    try {
      await createMutation.mutateAsync({
        data: { trainerId, gymId, locationId, dayOfWeek: dayIndex, startTime, endTime },
      });
      onChanged();
      setAdding(false);
    } catch (err: unknown) {
      toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" });
    }
  };

  const handleDelete = async (blockId: string) => {
    try {
      await deleteMutation.mutateAsync({ id: blockId });
      onChanged();
    } catch {
      toast({ title: "Error", description: "Failed to remove availability block", variant: "destructive" });
    }
  };

  return (
    <div className="py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-white w-24">{day}</span>
        {dayBlocks.length === 0 && !adding && (
          <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">
            No slots
          </Badge>
        )}
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding(true)}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {dayBlocks.map(block => (
        <div key={block.id} className="flex items-center gap-2 ml-2 mb-1.5">
          <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm text-white">
            {formatTimeDisplay(block.startTime)} — {formatTimeDisplay(block.endTime)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-auto text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={() => handleDelete(block.id)}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}

      {adding && (
        <div className="flex items-center gap-2 ml-2 mt-2">
          <Select value={startTime} onValueChange={setStartTime}>
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {TIME_OPTIONS.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">to</span>
          <Select value={endTime} onValueChange={setEndTime}>
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {TIME_OPTIONS.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 text-xs" onClick={handleAdd} disabled={createMutation.isPending}>
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function TrainerAvailabilityEditor({
  trainer,
  gymId,
}: {
  trainer: TrainerProfile;
  gymId: string;
}) {
  const queryClient = useQueryClient();

  const { data: locations = [] } = useListLocations(gymId, {
    query: { enabled: !!gymId },
  });

  const effectiveLocationId = useMemo(() => {
    const active = (locations as Location[]).filter(l => l.isActive);
    if (trainer.primaryLocationId) {
      const trainerLoc = active.find(l => l.id === trainer.primaryLocationId);
      if (trainerLoc) return trainerLoc.id;
    }
    const primary = active.find(l => l.isPrimary);
    return primary?.id ?? active[0]?.id;
  }, [locations, trainer.primaryLocationId]);

  const { data: blocks = [], isLoading } = useGetTrainerAvailability(trainer.id, undefined, {
    query: { enabled: !!trainer.id },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetTrainerAvailabilityQueryKey(trainer.id) });
  }, [queryClient, trainer.id]);

  const activeBlocks = useMemo(() => (blocks as AvailabilityBlock[]).filter(b => b.isActive), [blocks]);

  const totalSlots = activeBlocks.length;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/5">
        <UserCheck className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">
            {trainer.firstName} {trainer.lastName}
          </p>
          <p className="text-xs text-muted-foreground">
            {totalSlots} availability slot{totalSlots !== 1 ? "s" : ""} configured
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-white/[0.02] border border-white/5 px-4">
        {DAY_NAMES.map((day, i) => (
          <TrainerDayRow
            key={i}
            day={DAY_SHORT[i]}
            dayIndex={i}
            blocks={activeBlocks}
            trainerId={trainer.id}
            gymId={gymId}
            locationId={effectiveLocationId}
            onChanged={invalidate}
          />
        ))}
      </div>
    </div>
  );
}

export function TrainerAvailabilityPanel({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const gymId = user?.gymId;
  const [selectedTrainerId, setSelectedTrainerId] = useState<string | null>(null);

  const { data: trainers = [], isLoading: trainersLoading } = useListTrainers(gymId ?? "", undefined, {
    query: { enabled: !!gymId },
  });

  const activeTrainers = (trainers as TrainerProfile[]).filter(t => t.id);
  const selectedTrainer = activeTrainers.find(t => t.id === selectedTrainerId) ?? activeTrainers[0] ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-background border-white/5 overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Trainer Availability
          </SheetTitle>
          <SheetDescription>
            Manage weekly availability for your trainers.
          </SheetDescription>
        </SheetHeader>

        {trainersLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTrainers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <Users className="w-10 h-10 text-muted-foreground" />
            <p className="text-muted-foreground">No trainers found.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <Select
              value={selectedTrainer?.id ?? ""}
              onValueChange={setSelectedTrainerId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a trainer" />
              </SelectTrigger>
              <SelectContent>
                {activeTrainers.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5" />
                      {t.firstName} {t.lastName}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedTrainer && gymId && (
              <TrainerAvailabilityEditor
                key={selectedTrainer.id}
                trainer={selectedTrainer}
                gymId={gymId}
              />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function useTrainerAvailabilityForCalendar(selectedTrainerId: string | null) {
  const { data: blocks = [] } = useGetTrainerAvailability(selectedTrainerId ?? "", undefined, {
    query: { enabled: !!selectedTrainerId },
  });

  const activeBlocks = useMemo(
    () => (blocks as AvailabilityBlock[]).filter(b => b.isActive),
    [blocks]
  );

  return activeBlocks;
}
